import {
  ActionIcon,
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  CloseButton,
  Drawer,
  Group,
  Pagination,
  Rating,
  Select,
  Skeleton,
  Stack,
  Tabs,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { IconAdjustmentsHorizontal, IconMessageCircle, IconRefresh, IconSearch } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import moment from "moment-jalaali";
import persian from "react-date-object/calendars/persian";
import persianFa from "react-date-object/locales/persian_fa";
import DatePicker, { DateObject } from "react-multi-date-picker";
import useIsSmall from "@/hooks/useIsSmall";
import { useReviewAccess } from "@/services/reviews/useReviewAccess";
import useGetMerchantUsers from "@/services/merchantUser/hooks/useGetMerchantUsers";
import { useGetMerchantServices } from "@/services/appointment/hooks/useGetMerchantServices";
import type {
  MerchantReviewListParams,
  ReviewConsentState,
  ReviewModerationStatus,
  ReviewReplyState,
  ReviewSort,
  ReviewVisibilityStatus,
} from "@/models/club";
import { reviews, reviewKeys } from "@/services/reviews";
import {
  blockerLabel,
  consentLabels,
  moderationLabels,
  replyLabels,
  reviewError,
  visibilityLabels,
} from "@/services/reviews/policy";
import ReviewDetail from "./ReviewDetail";
import { moderationColor, visibilityColor } from "./reviewStyle";

const PAGE_SIZE = 20;
const options = (values: Record<string, string>) =>
  Object.entries(values).map(([value, label]) => ({ value, label }));
const ratingOptions = [5, 4, 3, 2, 1].map((n) => ({ value: String(n), label: `${n.toLocaleString("fa-IR")} از ۵` }));
const sortOptions = [
  { value: "newest", label: "جدیدترین" },
  { value: "oldest", label: "قدیمی‌ترین" },
  { value: "highestRated", label: "بالاترین امتیاز" },
  { value: "lowestRated", label: "پایین‌ترین امتیاز" },
];

type Tab = "all" | "pending" | "approved" | "rejected" | "published";
// Everything except status, search and sort; edited in the drawer and applied together.
type Filters = {
  Visibility?: ReviewVisibilityStatus;
  PublicConsent?: ReviewConsentState;
  ReplyState?: ReviewReplyState;
  VisitRating?: number;
  ProfessionalId?: number;
  ProfessionalRating?: number;
  ServiceId?: number;
  // Jalali range picked in the drawer, sent as UTC day bounds.
  range?: [string, string];
};

export default function SalonReviews() {
  const access = useReviewAccess();
  const isSmall = useIsSmall();
  const team = useGetMerchantUsers();
  const services = useGetMerchantServices(access.canRead);
  const [tab, setTab] = useState<Tab>("all");
  const [applied, setApplied] = useState<Filters>({});
  const [draft, setDraft] = useState<Filters>({});
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sort, setSort] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [query] = useDebouncedValue(search.trim(), 350);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<number>();
  const [dirty, setDirty] = useState(false);

  const teamOptions = useMemo(
    () =>
      (team.data ?? [])
        .filter((item) => item.id)
        .map((item) => ({
          value: String(item.id),
          label: `${item.firstName || ""} ${item.lastName || ""}`.trim() || `متخصص ${item.id}`,
        })),
    [team.data],
  );
  const serviceOptions = useMemo(
    () =>
      (services.data ?? [])
        .flatMap((category) => category.services ?? [])
        .filter((item) => item.id)
        .map((item) => ({ value: String(item.id), label: item.title || `خدمت ${item.id}` })),
    [services.data],
  );

  const params: MerchantReviewListParams = {
    Status: tab === "pending" || tab === "approved" || tab === "rejected" ? (tab as ReviewModerationStatus) : undefined,
    Visibility: tab === "published" ? ("published" as ReviewVisibilityStatus) : applied.Visibility,
    PublicConsent: applied.PublicConsent,
    ReplyState: applied.ReplyState,
    VisitRating: applied.VisitRating,
    ProfessionalId: applied.ProfessionalId,
    ProfessionalRating: applied.ProfessionalId ? applied.ProfessionalRating : undefined,
    ServiceId: applied.ServiceId,
    FromUtc: applied.range?.[0],
    ToUtc: applied.range?.[1],
    Query: query || undefined,
    Sort: (sort as ReviewSort) || undefined,
    Count: PAGE_SIZE,
    Skip: (page - 1) * PAGE_SIZE,
  };
  const list = useQuery({
    queryKey: [...reviewKeys.all, "list", params],
    queryFn: () => reviews.list(params),
    enabled: access.canRead,
    retry: false,
    keepPreviousData: true,
  });

  const label = (value: string | undefined, values: { value: string; label: string }[]) =>
    values.find((item) => item.value === value)?.label ?? value;
  // Applied filters as removable chips under the toolbar.
  const chips: { key: keyof Filters; text: string }[] = [
    applied.Visibility && tab !== "published" && { key: "Visibility" as const, text: `نمایش: ${visibilityLabels[applied.Visibility]}` },
    applied.ReplyState && { key: "ReplyState" as const, text: `پاسخ: ${replyLabels[applied.ReplyState]}` },
    applied.PublicConsent && { key: "PublicConsent" as const, text: `رضایت: ${consentLabels[applied.PublicConsent]}` },
    applied.VisitRating && { key: "VisitRating" as const, text: `امتیاز مراجعه: ${applied.VisitRating.toLocaleString("fa-IR")}` },
    applied.ProfessionalId && { key: "ProfessionalId" as const, text: `متخصص: ${label(String(applied.ProfessionalId), teamOptions)}${applied.ProfessionalRating ? ` (${applied.ProfessionalRating.toLocaleString("fa-IR")} از ۵)` : ""}` },
    applied.ServiceId && { key: "ServiceId" as const, text: `خدمت: ${label(String(applied.ServiceId), serviceOptions)}` },
    applied.range && { key: "range" as const, text: `${moment(applied.range[0]).format("jYYYY/jMM/jDD")} تا ${moment(applied.range[1]).format("jYYYY/jMM/jDD")}` },
  ].filter(Boolean) as { key: keyof Filters; text: string }[];

  const applyFilters = (next: Filters) => {
    setApplied(next);
    setPage(1);
  };
  const removeChip = (key: keyof Filters) =>
    applyFilters({ ...applied, [key]: undefined, ...(key === "ProfessionalId" ? { ProfessionalRating: undefined } : {}) });
  const openFilters = () => {
    setDraft(applied);
    setFiltersOpen(true);
  };
  const setDraftValue = (key: keyof Filters, value: string | null, numeric = false) =>
    setDraft((old) => ({
      ...old,
      [key]: value ? (numeric ? Number(value) : value) : undefined,
      ...(key === "ProfessionalId" && !value ? { ProfessionalRating: undefined } : {}),
    }));

  const closeDetail = () => {
    if (!dirty || window.confirm("تغییرات ذخیره‌نشده پاسخ حذف شود؟")) {
      setSelected(undefined);
      setDirty(false);
    }
  };

  if (access.isLoading || (access.isManager && access.capabilities.isLoading))
    return (
      <Stack p="md">
        <Skeleton h={32} w={220} />
        <Skeleton h={40} />
        <Skeleton h={160} />
      </Stack>
    );
  if (access.capabilities.isError)
    return (
      <Alert color="red" m="md">
        {reviewError(access.capabilities.error)}
        <Button mt="xs" size="xs" onClick={() => void access.capabilities.refetch()}>
          تلاش مجدد
        </Button>
      </Alert>
    );
  if (!access.canRead)
    return (
      <Alert color="yellow" m="md">
        {access.isManager
          ? blockerLabel(access.capabilities.data?.disabledReason) || "مدیریت نظرات برای این مجموعه فعال نیست."
          : "مدیریت نظرات فقط برای مدیر سالن در دسترس است."}
      </Alert>
    );

  const counts = list.data?.counts;
  const count = (value?: number) => (value == null ? "" : value.toLocaleString("fa-IR"));
  const total = list.data?.totalCount ?? 0;
  const draftCount = Object.values({ ...draft, ProfessionalRating: undefined }).filter((value) => value !== undefined).length;

  return (
    <Stack p={isSmall ? "sm" : "md"} gap="md" dir="rtl">
      <Group justify="space-between" align="flex-end">
        <div>
          <Title order={3}>نظرات مشتریان</Title>
          <Text size="sm" c="dimmed">
            نظرها را بررسی و تأیید کنید، در سایت منتشر کنید و به مشتری پاسخ دهید.
          </Text>
        </div>
        <ActionIcon variant="subtle" aria-label="به‌روزرسانی" loading={list.isFetching} onClick={() => void list.refetch()}>
          <IconRefresh size={18} />
        </ActionIcon>
      </Group>

      <Tabs value={tab} onChange={(value) => { setTab((value as Tab) || "all"); setPage(1); }} variant="pills" radius="xl">
        <Tabs.List style={{ flexWrap: isSmall ? "nowrap" : "wrap", overflowX: "auto" }}>
          <Tabs.Tab value="all">همه</Tabs.Tab>
          <Tabs.Tab value="pending" rightSection={counts?.pending ? <Badge size="sm" color="yellow" variant="light">{count(counts.pending)}</Badge> : null}>
            در انتظار بررسی
          </Tabs.Tab>
          <Tabs.Tab value="approved" rightSection={<Text span size="xs" c="dimmed">{count(counts?.approved)}</Text>}>تأییدشده</Tabs.Tab>
          <Tabs.Tab value="rejected" rightSection={<Text span size="xs" c="dimmed">{count(counts?.rejected)}</Text>}>ردشده</Tabs.Tab>
          <Tabs.Tab value="published" rightSection={<Text span size="xs" c="dimmed">{count(counts?.published)}</Text>}>منتشرشده</Tabs.Tab>
        </Tabs.List>
      </Tabs>

      <Group gap="xs" wrap="nowrap">
        <TextInput
          style={{ flex: 1 }}
          leftSection={<IconSearch size={16} />}
          placeholder="جستجو در متن، نام یا شماره مشتری"
          value={search}
          onChange={(e) => {
            setSearch(e.currentTarget.value);
            setPage(1);
          }}
          rightSection={search ? <CloseButton size="sm" aria-label="پاک کردن جستجو" onClick={() => setSearch("")} /> : null}
        />
        {!isSmall && (
          <Select w={170} placeholder="مرتب‌سازی" data={sortOptions} value={sort} onChange={(value) => { setSort(value); setPage(1); }} clearable aria-label="مرتب‌سازی" />
        )}
        <Button
          variant={chips.length ? "light" : "default"}
          leftSection={<IconAdjustmentsHorizontal size={16} />}
          onClick={openFilters}
          px={isSmall ? "sm" : undefined}
        >
          فیلترها
          {chips.length > 0 && <Badge size="sm" circle ms={6}>{chips.length.toLocaleString("fa-IR")}</Badge>}
        </Button>
      </Group>

      {chips.length > 0 && (
        <Group gap={6}>
          {chips.map((chip) => (
            <Badge key={chip.key} variant="light" size="lg" radius="sm" tt="none" fw={500}
              rightSection={<CloseButton size="xs" aria-label={`حذف ${chip.text}`} onClick={() => removeChip(chip.key)} />}>
              {chip.text}
            </Badge>
          ))}
          <Button variant="subtle" size="compact-sm" onClick={() => applyFilters({})}>پاک کردن همه</Button>
        </Group>
      )}

      {list.isError && (
        <Alert color="red">
          {reviewError(list.error)}
          <Button variant="subtle" size="xs" onClick={() => void list.refetch()}>تلاش مجدد</Button>
        </Alert>
      )}
      {list.isLoading && (
        <Stack>
          {[0, 1, 2].map((i) => <Skeleton key={i} h={150} radius="md" />)}
        </Stack>
      )}
      {list.isSuccess && list.data.items?.length === 0 && (
        <Card withBorder radius="md" py="xl">
          <Stack align="center" gap={6}>
            <IconMessageCircle size={36} stroke={1.2} color="var(--mantine-color-dimmed)" />
            <Text fw={600}>{tab === "pending" && !chips.length && !query ? "نظری در انتظار بررسی نیست" : "با این فیلترها نظری پیدا نشد"}</Text>
            {(chips.length > 0 || query || tab !== "all") && (
              <Button variant="subtle" size="xs" onClick={() => { applyFilters({}); setSearch(""); setTab("all"); }}>
                نمایش همه نظرها
              </Button>
            )}
          </Stack>
        </Card>
      )}

      <Stack gap="sm" style={{ opacity: list.isPreviousData ? 0.6 : 1 }}>
        {list.data?.items?.map((item) => {
          const name = [item.customer?.firstName, item.customer?.lastName].filter(Boolean).join(" ") || item.displayName || item.customer?.phoneNumber || "مشتری";
          const canPublishNext = item.moderationStatus === "approved" && item.visibilityStatus !== "published" && item.publicConsent === "granted";
          const cta = item.moderationStatus === "pending" ? "بررسی و تأیید" : canPublishNext ? "انتشار در سایت" : "مشاهده و پاسخ";
          return (
            <Card key={item.id} withBorder radius="md" padding="md"
              style={item.moderationStatus === "pending" ? { borderInlineStart: "3px solid var(--mantine-color-yellow-5)" } : undefined}>
              <Stack gap="xs">
                <Group justify="space-between" wrap="nowrap" align="flex-start">
                  <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
                    <Avatar radius="xl" color="myColor">{name.charAt(0)}</Avatar>
                    <div style={{ minWidth: 0 }}>
                      <Text fw={600} truncate>{name}</Text>
                      <Text size="xs" c="dimmed">
                        {item.submittedAtUtc ? moment(item.submittedAtUtc).format("jYYYY/jMM/jDD · HH:mm") : "—"}
                      </Text>
                    </div>
                  </Group>
                  <Group gap={4} wrap="nowrap">
                    <Badge color={moderationColor(item.moderationStatus)} variant="light">
                      {moderationLabels[item.moderationStatus ?? ""] || "نامشخص"}
                    </Badge>
                    {item.visibilityStatus === "published" && (
                      <Badge color={visibilityColor(item.visibilityStatus)} variant="dot">{visibilityLabels.published}</Badge>
                    )}
                  </Group>
                </Group>
                <Group gap={6}>
                  <Rating readOnly value={item.visitRating ?? 0} size="sm" />
                  <Text size="xs" c="dimmed">امتیاز مراجعه</Text>
                </Group>
                <Text size="sm" lineClamp={3} style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }} c={item.text ? undefined : "dimmed"}>
                  {item.text || "نظر بدون متن"}
                </Text>
                {(item.serviceTitles?.length || item.professionalRatings?.length) ? (
                  <Group gap={6}>
                    {item.serviceTitles?.map((title) => <Badge key={title} variant="outline" color="gray" tt="none" fw={500}>{title}</Badge>)}
                    {item.professionalRatings?.map((person) => (
                      <Badge key={person.merchantUserId} variant="light" color="gray" tt="none" fw={500}>
                        {person.displayName || `متخصص ${person.merchantUserId}`} · {(person.rating ?? 0).toLocaleString("fa-IR")}★
                      </Badge>
                    ))}
                  </Group>
                ) : null}
                <Group justify="space-between" mt={4}>
                  <Text size="xs" c="dimmed">
                    {item.replyState === "published" ? "پاسخ داده شده" : item.replyState === "draft" ? "پیش‌نویس پاسخ" : "بدون پاسخ"}
                    {item.publicConsent !== "granted" && " · مشتری اجازه انتشار نداده"}
                  </Text>
                  <Button size="xs" variant={item.moderationStatus === "pending" || canPublishNext ? "filled" : "light"}
                    onClick={() => setSelected(item.id)} disabled={!item.id}>
                    {cta}
                  </Button>
                </Group>
              </Stack>
            </Card>
          );
        })}
      </Stack>

      {total > PAGE_SIZE && (
        <Group justify="center">
          <Pagination total={Math.ceil(total / PAGE_SIZE)} value={page} onChange={setPage} size={isSmall ? "sm" : "md"} />
        </Group>
      )}

      <Drawer
        opened={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        title="فیلتر نظرها"
        // In RTL Mantine's "left" sits on the right edge; slide in from that edge.
        position={isSmall ? "bottom" : "left"}
        transitionProps={isSmall ? undefined : { transition: "slide-left" }}
        size={isSmall ? "85%" : 380}
        radius={isSmall ? "md" : 0}
        dir="rtl"
      >
        <Stack gap="sm" pb="xl">
          {isSmall && (
            <Select label="مرتب‌سازی" placeholder="جدیدترین" data={sortOptions} value={sort} clearable
              onChange={(value) => { setSort(value); setPage(1); }} comboboxProps={{ withinPortal: true, zIndex: 400 }} />
          )}
          <Select label="امتیاز مراجعه" placeholder="همه" clearable data={ratingOptions}
            value={draft.VisitRating?.toString() ?? null} onChange={(v) => setDraftValue("VisitRating", v, true)} comboboxProps={{ zIndex: 400 }} />
          <Select label="متخصص" placeholder="همه" searchable clearable data={teamOptions}
            value={draft.ProfessionalId?.toString() ?? null} onChange={(v) => setDraftValue("ProfessionalId", v, true)} comboboxProps={{ zIndex: 400 }} />
          <Select label="امتیاز همان متخصص" placeholder={draft.ProfessionalId ? "همه" : "ابتدا متخصص را انتخاب کنید"} clearable
            disabled={!draft.ProfessionalId} data={ratingOptions}
            value={draft.ProfessionalRating?.toString() ?? null} onChange={(v) => setDraftValue("ProfessionalRating", v, true)} comboboxProps={{ zIndex: 400 }} />
          <Select label="خدمت" placeholder="همه" searchable clearable data={serviceOptions}
            value={draft.ServiceId?.toString() ?? null} onChange={(v) => setDraftValue("ServiceId", v, true)} comboboxProps={{ zIndex: 400 }} />
          <DatePicker
            calendar={persian}
            locale={persianFa}
            range
            portal
            zIndex={400}
            value={draft.range ? draft.range.map((iso) => new DateObject({ date: new Date(iso), calendar: persian, locale: persianFa })) : []}
            onChange={(value) => {
              const dates = Array.isArray(value) ? value : [];
              if (dates.length === 2)
                setDraft((old) => ({
                  ...old,
                  range: [moment(dates[0].toDate()).startOf("day").toISOString(), moment(dates[1].toDate()).endOf("day").toISOString()],
                }));
              else if (dates.length === 0) setDraft((old) => ({ ...old, range: undefined }));
            }}
            render={(_value, open) => (
              <TextInput label="بازه تاریخ ثبت نظر" placeholder="انتخاب بازه" readOnly onClick={open}
                value={draft.range ? `${moment(draft.range[0]).format("jYYYY/jMM/jDD")} تا ${moment(draft.range[1]).format("jYYYY/jMM/jDD")}` : ""}
                rightSection={draft.range ? <CloseButton size="sm" aria-label="پاک کردن تاریخ" onClick={(e) => { e.stopPropagation(); setDraft((old) => ({ ...old, range: undefined })); }} /> : null} />
            )}
          />
          <Select label="نمایش در سایت" placeholder={tab === "published" ? "زبانه «منتشرشده» فعال است" : "همه"} clearable disabled={tab === "published"}
            data={options(visibilityLabels)} value={draft.Visibility ?? null} onChange={(v) => setDraftValue("Visibility", v)} comboboxProps={{ zIndex: 400 }} />
          <Select label="وضعیت پاسخ" placeholder="همه" clearable data={options(replyLabels)}
            value={draft.ReplyState ?? null} onChange={(v) => setDraftValue("ReplyState", v)} comboboxProps={{ zIndex: 400 }} />
          <Select label="اجازه انتشار مشتری" placeholder="همه" clearable data={options(consentLabels)}
            value={draft.PublicConsent ?? null} onChange={(v) => setDraftValue("PublicConsent", v)} comboboxProps={{ zIndex: 400 }} />
          <Group grow mt="sm" style={{ position: "sticky", bottom: 0, background: "var(--mantine-color-body)", paddingBlock: 8 }}>
            <Button variant="default" onClick={() => setDraft({})} disabled={!draftCount}>پاک کردن</Button>
            <Button onClick={() => { applyFilters(draft); setFiltersOpen(false); }}>
              اعمال فیلترها
            </Button>
          </Group>
        </Stack>
      </Drawer>

      <Drawer
        opened={selected !== undefined}
        onClose={closeDetail}
        title="بررسی نظر مشتری"
        // Opens on the physical left: in RTL Mantine's "right" is the left edge, sliding in from it.
        position={isSmall ? "bottom" : "right"}
        transitionProps={isSmall ? undefined : { transition: "slide-right" }}
        size={isSmall ? "94%" : 600}
        radius={isSmall ? "md" : 0}
        closeOnClickOutside={!dirty}
        dir="rtl"
      >
        {selected !== undefined && (
          <ReviewDetail key={selected} reviewId={selected} onDirtyChange={setDirty} />
        )}
      </Drawer>
    </Stack>
  );
}
